/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { AuthenticationService, HttpResponse, Setting, Logger } from '@app/core';
import { Observable } from 'rxjs';
import { User } from '@app/settings/users/user.model';
import { ApiQuery } from '@app/core/http/api-query';
import { Model } from '@app/shared/models/model.model';

const log = new Logger('ProfileService');

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  constructor(private http: HttpClient, private authService: AuthenticationService) {}

  /**
   * Set profile setting
   * @param name Setting name
   * @param value Setting value
   * @param collection Setting collection
   */
  setSetting(name: string, value: any, collection: string): Observable<HttpResponse> {
    const postData = {
      settings: [
        {
          name: name,
          value: value,
          collection: collection,
        },
      ],
    };

    return this.http.post('v1/profile', postData).pipe(
      map((response: HttpResponse) => {
        this.authService.setProfile(response.data);
        return response;
      })
    );
  }

  /**
   * Връща всички настройки
   */
  getSettings() {
    log.debug('getSettings');
    return this.authService.accessToken.profile.settings;
  }

  /**
   * Връща конкретна настройка
   *
   * @param name Setting name
   * @param collection Setting collection
   * @param defaultValue Default value (false)
   * @return boolean
   */
  getSetting(name: string, collection: string, defaultValue: any = false) {
    const setting = this.authService.accessToken.profile.settings.find((s: Setting) => {
      return s.name === name && s.collection === collection;
    });

    if (setting) {
      return setting.value;
    }

    return defaultValue;
  }

  update(user: User): Observable<User> {
    return this.http.post('v1/profile', user).pipe(
      map((response: HttpResponse) => {
        return new User().deserialize(response.data);
      })
    );
  }

  show(id: number, query: ApiQuery = new ApiQuery()): Observable<Model> {
    return this.http.get('v1/profile', query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        return new User().deserialize(response.data);
      })
    );
  }

  /**
   * Change password
   *
   * @param passwords Object - Old password, new password and new password confirmation
   */
  changePassword(passwords: any): Observable<User> {
    return this.http.post('v1/profile/change_password', passwords).pipe(
      map((response: any) => {
        log.debug(response);
        return response.status;
      })
    );
  }
}
